import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { articlesService, subUnionsService, projectsService } from '../../../services/dataService';
import { formatShortDate } from '../../../utils/dateUtils';

const RecentContentTable = () => {
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRecentContent = async () => {
      try {
        setLoading(true);
        
        // Fetch recent items from different services
        const [articles, subUnions, projects] = await Promise.all([
          articlesService?.getAll()?.catch(() => []),
          subUnionsService?.getAll()?.catch(() => []),
          projectsService?.getAll()?.catch(() => [])
        ]);

        // Combine and sort by creation date
        const combinedItems = [
          ...articles?.slice(0, 3)?.map(item => ({
            id: item?.id,
            title: item?.titleAr || item?.titleEn || 'مقال بدون عنوان',
            type: 'article',
            typeLabel: 'مقال',
            date: item?.createdAt,
            status: item?.published ? 'منشور' : 'مسودة',
            statusColor: item?.published ? 'success' : 'warning',
            icon: 'FileText'
          })),
          ...subUnions?.slice(0, 3)?.map(item => ({
            id: item?.id,
            title: item?.nameAr || item?.nameEn || 'اتحاد بدون اسم',
            type: 'sub_union',
            typeLabel: 'اتحاد فرعي',
            date: item?.createdAt,
            status: item?.status === 'active' ? 'نشط' : 'غير نشط',
            statusColor: item?.status === 'active' ? 'success' : 'error',
            icon: 'Building2'
          })),
          ...projects?.slice(0, 3)?.map(item => ({
            id: item?.id,
            title: item?.titleAr || item?.titleEn || 'مشروع بدون عنوان',
            type: 'project',
            typeLabel: 'مشروع',
            date: item?.createdAt,
            status: getProjectStatusLabel(item?.status),
            statusColor: getProjectStatusColor(item?.status),
            icon: 'FolderOpen'
          }))
        ]?.sort((a, b) => new Date(b.date) - new Date(a.date))?.slice(0, 8); // Show latest 8 items

        setRecentItems(combinedItems);
      } catch (err) {
        console.error('Error fetching recent content:', err);
        setError('فشل في تحميل المحتوى الحديث');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentContent();
  }, []);

  const getProjectStatusLabel = (status) => {
    switch (status) {
      case 'ongoing': return 'قيد التنفيذ';
      case 'completed': return 'مكتمل';
      case 'planned': return 'مخطط';
      default: return 'غير محدد';
    }
  };

  const getProjectStatusColor = (status) => {
    switch (status) {
      case 'ongoing': return 'primary';
      case 'completed': return 'success';
      case 'planned': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusBadgeClasses = (color) => {
    switch (color) {
      case 'success':
        return 'bg-success/10 text-success border-success/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'error':
        return 'bg-error/10 text-error border-error/20';
      case 'primary':
        return 'bg-primary/10 text-primary border-primary/20';
      default:
        return 'bg-secondary/10 text-secondary border-secondary/20';
    }
  };


  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            المحتوى الحديث
          </h3>
          <Icon name="Clock" size={20} className="text-muted-foreground" />
        </div>
        <div className="space-y-4">
          {[...Array(5)]?.map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
                  <div className="w-8 h-8 bg-muted rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
                <div className="w-16 h-6 bg-muted rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error font-medium mb-2">{error}</p>
          <button 
            onClick={() => window.location?.reload()} 
            className="text-sm text-primary hover:text-primary/80 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground font-arabic">
          المحتوى الحديث
        </h3>
        <Icon name="Clock" size={20} className="text-muted-foreground" />
      </div>
      {recentItems?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">لا يوجد محتوى حديث</p>
        </div>
      ) : (
        <div className="space-y-3">
          {recentItems?.map((item, index) => (
            <div 
              key={`${item?.type}-${item?.id}-${index}`}
              className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={item?.icon} size={16} className="text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground font-arabic truncate">
                    {item?.title}
                  </p>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <span className="text-xs text-muted-foreground font-caption">
                      {item?.typeLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground font-caption">
                      {formatShortDate(item?.date)}
                    </span>
                  </div>
                </div>
              </div>
              
              <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getStatusBadgeClasses(item?.statusColor)}`}>
                {item?.status}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-border">
        <button className="text-sm text-primary hover:text-primary/80 transition-colors font-medium">
          عرض جميع العناصر
        </button>
      </div>
    </div>
  );
};

export default RecentContentTable;